import React, { useEffect, useState, useCallback } from "react";
import { AxiosError } from "axios";
import { isPossiblePhoneNumber } from "react-phone-number-input";

import {
  IShowRegistrationSummary,
  IShow,
  IDogClass,
  IEvent,
  IBreed,
  IAddress,
  IDogCoownerInfo,
  IDog
} from "@eagerdog/interfaces";

import { Constants } from "@eagerdog/constants";

import { apiService } from "src/services/api.service";
import {
  nonLicensed,
  dogObedienceHeights,
  dogRallyHeights,
  dogAgilityHeights,
  dogHeightTypes,
  helperService
} from "src/services/helper.service";

import num from "src/assets/icons/num.svg";
import scale from "src/assets/icons/scale.svg";
import star from "src/assets/icons/star.svg";

import StepForm, { Step, useStepForm } from "src/components/StepForm/StepForm";
import FormBuilder from "src/components/FormBuilder/FormBuilder";
import Input from "src/components/Input/Input";
import Checkbox from "src/components/Checkbox/Checkbox";
import Dropdown, { IOption } from "src/components/Dropdown/Dropdown";
import DatePick from "src/components/DatePick/DatePick";
import Phone from "src/components/Phone/Phone";
import SearchDown from "src/components/SearchDown/SearchDown";
import OwnerList from "src/components/OwnerList/OwnerList";
import UserAddress, { isValidAddress, isValidPhone, IAddressError } from "src/routes/EventRegister/UserAddress/UserAddress";
import { toast } from "src/components/Toast/ToastManager";

import { workingOrCourierOptions, masterOptions, getWorkingLegOptions } from "src/routes/EventRegister/utils/WaterDog"
import { dogWheelchairOptions } from "src/routes/EventRegister/utils/CwagsScent"

import "./EditRegistration.scss";

interface IProps {
  event: IEvent | undefined,
  registration: IShowRegistrationSummary,
  onComplete(): void
}

const EditRegistration: React.FC<IProps> = (props) => {
  const [loaded, setLoaded] = useState<boolean>(false);
  const { switchToTab, activeTab } = useStepForm();

  const [show, setShow] = useState<IShow>();
  const [dogClasses, setDogClasses] = useState<any[]>([]);
  const [isUpdatingRegistration, setIsUpdatingRegistration] = useState<boolean>(false);

  const [callName, setCallName] = useState<string>(props.registration?.dog?.call_name || "");
  const [name, setName] = useState<string>(props.registration?.dog?.registered_name || "");
  const [prefixTitle, setPrefixTitle] = useState<string>(props.registration?.dog?.prefix_title || "");
  const [suffixTitle, setSuffixTitle] = useState<string>(props.registration?.dog?.suffix_title || "");

  const [sire, setSire] = useState<string>(props.registration?.dog?.sire || "");
  const [dam, setDam] = useState<string>(props.registration?.dog?.dam || "");
  const [finalResponse, setFinalResponse] = useState<string>(props.registration?.dog?.final_response || "");
  const [countryOfBirth, setCountryOfBirth] = useState<string>(props.registration?.dog?.country_of_birth || "");

  const [jumpHeightType, setJumpHeightType] = useState<IOption>({ id: "None", value: "None" });
  const [oJumpHeight, setOJumpHeight] = useState<IOption>({
    id: props.registration?.dog?.obedience_jump_height?.toString() || "",
    value: props.registration?.dog?.obedience_jump_height ? `${props.registration.dog.obedience_jump_height}"` : '""'
  });
  const [oPreferredJumpHeight, setOPreferredJumpHeight] = useState<IOption>({
    id: props.registration?.dog?.akc_obedience_preferred_jump_height?.toString() || "",
    value: props.registration?.dog?.akc_obedience_preferred_jump_height ? `${props.registration.dog.akc_obedience_preferred_jump_height}"` : '""'
  });
  const [rJumpHeight, setRJumpHeight] = useState<IOption>({
    id: props.registration?.dog?.rally_jump_height?.toString() || "",
    value: props.registration?.dog?.rally_jump_height ? `${props.registration.dog.rally_jump_height}"` : '""'
  });
  const [agilityJumpHeight, setAgilityJumpHeight] = useState<IOption>({
    id: props.registration?.dog?.agility_jump_height?.toString() || "",
    value: props.registration?.dog?.agility_jump_height ? `${props.registration.dog.agility_jump_height}"` : '""'
  });

  const [breeder, setBreeder] = useState<string>(props.registration.dog.breeder || "");

  const [regNum, setRegNum] = useState<string>(props.registration.dog.registration_number || "");
  const [regPrefix, setRegPrefix] = useState<IOption>({ id: props.registration.dog.registration_number_type || "None", value: props.registration.dog.registration_number_type || "None" });

  const [dogDOB, setDogDOB] = useState(props.registration.dog.dob ? new Date(props.registration.dog.dob) : undefined);
  const [dateReceived, setDateReceived] = useState<Date | undefined>(
    props.registration.date_received ? new Date(props.registration.date_received) : undefined
  );
  const [gender, setGender] = useState<string>(props.registration.dog.sex || "");

  const [weight, setWeight] = useState<number | undefined>(props.registration.dog.weight || undefined);
  const [heightAtWithers, setHeightAtWithers] = useState<number | undefined>(props.registration.dog.height_at_withers || undefined);
  const [heightAtWithersInput, setHeightAtWithersInput] = useState<string>(props.registration.dog.height_at_withers?.toString() || "");

  const [selectedBreed, setSelectedBreed] = useState<IBreed>();
  const [breedSearch, setBreedSearch] = useState<string>(props.registration.dog.breed || "");
  const [breedList, setBreedList] = useState<IBreed[]>([]);
  const [variety, setVariety] = useState<IOption>(props.registration.dog.variety ? { id: props.registration.dog.variety, value: props.registration.dog.variety } : { id: "None", value: "None" });

  const [AKCVariation, setAKCVariation] = useState<IOption>({ id: "None", value: "None" });
  const [UKCVariation, setUKCVariation] = useState<IOption>({ id: "None", value: "None" });

  const [displayName, setDisplayName] = useState<string>(props.registration.owner.display_name || "");
  const [email, setEmail] = useState<string>(props.registration.owner.email || "");
  
  const [address, setAddress] = useState<IAddress>(props.registration.owner.address || {});
  const [phone, setPhone] = useState<string>(props.registration.owner.phone || "");
  const isJuniorRegistration = !!props.registration.owner.is_junior;
  const [juniorNumber, setJuniorNumber] = useState<string>(props.registration.owner.junior_number || "");

  const [usingAgent, setUsingAgent] = useState<boolean | undefined>(props.registration.using_agent);
  const [agentName, setAgentName] = useState<string | undefined>(props.registration.agent_name);
  const [agentNumber, setAgentNumber] = useState<string | undefined>(props.registration.agent_number);
  const [agentEmail, setAgentEmail] = useState<string | undefined>(props.registration.agent_email);
  const [agentPhone, setAgentPhone] = useState<string | undefined>(props.registration.agent_phone);
  const [usingNonAgent, setUsingNonAgent] = useState<boolean | undefined>(props.registration.using_non_agent);
  const [competingOwnerHandler, setCompetingOwnerHandler] = useState<boolean | undefined>(props.registration.competing_owner_handler);

  const [needsMeasurement, setNeedsMeasurement] = useState<boolean>(!!props.registration.needs_measurement);

  const [dogs, setDogs] = useState<IDog[]>([]);

  // Companion dog registration number (for UKC Brace entries)
  const [companionRegNum, setCompanionRegNum] = useState<string>(() => {
    const braceClass = props.registration.dog_classes?.find(
      (dc: IDogClass) => dc.level === Constants.dog_class_element_level.brace
    );
    return braceClass?.extra_registration_number || "";
  });

  const [stewardName, setStewardName] = useState<string | undefined>(props.registration.substitute_name);
  const [stewardPhone, setStewardPhone] = useState<string | undefined>(props.registration.substitute_phone);
  const [stewardEmail, setStewardEmail] = useState<string | undefined>(props.registration.substitute_email);

  const [legInfo, setLegInfo] = useState<IOption>(masterOptions.find((option) => option.value === props.registration.leg_info) ?? workingOrCourierOptions[0]);
  const [wheelchairType, setWheelchairType] = useState<IOption>(dogWheelchairOptions.find((options) => options.value === props.registration.wheelchair_type) ?? dogWheelchairOptions[0]);

  // CAT/FastCAT title state variables
  const akcCatTitles: IOption[] = [
    { value: Constants.akc_cat_titles.ca, id: Constants.akc_cat_titles.ca },
    { value: Constants.akc_cat_titles.caa, id: Constants.akc_cat_titles.caa },
    { value: Constants.akc_cat_titles.cax, id: Constants.akc_cat_titles.cax },
    { value: Constants.akc_cat_titles.cax2, id: Constants.akc_cat_titles.cax2 },
  ];

  const akcFastCatTitles: IOption[] = [
    { value: "FCAT", id: Constants.akc_fastcat_titles.fcat },
    { value: "DCAT - 500 pts", id: Constants.akc_fastcat_titles.dcat },
    { value: "BCAT - 150 pts", id: Constants.akc_fastcat_titles.bcat },
  ];

  const getTitleOptionsForShowType = (showType: string): IOption[] => {
    if (showType === Constants.show_type.cat) return akcCatTitles;
    return akcFastCatTitles;
  };

  const getDefaultTitleForShowType = (showType: string): IOption => {
    if (showType === Constants.show_type.cat) {
      return { value: Constants.akc_cat_titles.ca, id: Constants.akc_cat_titles.ca };
    }
    return { value: "BCAT - 150 pts", id: Constants.akc_fastcat_titles.bcat };
  };

  const [planToEarnTitle, setPlanToEarnTitle] = useState<boolean>(
    props.registration.plan_to_earn_title ?? !!props.registration.title
  );
  const [selectedTitle, setSelectedTitle] = useState<IOption>(() => {
    const titleOptions = getTitleOptionsForShowType(props.registration.show_type);
    if (props.registration.title) {
      // FCAT titles are stored as "FCAT30" etc. — match the base FCAT option
      if (props.registration.title.toUpperCase().startsWith(Constants.akc_fastcat_titles.fcat)) {
        return { value: "FCAT", id: Constants.akc_fastcat_titles.fcat };
      }
      const foundTitle = titleOptions.find(
        (option) =>
          option.id === props.registration.title ||
          option.value === props.registration.title
      );
      return foundTitle || getDefaultTitleForShowType(props.registration.show_type);
    }
    return getDefaultTitleForShowType(props.registration.show_type);
  });

  const [fcatNumber, setFcatNumber] = useState<number>(() => {
    if (props.registration.title?.toUpperCase().startsWith(Constants.akc_fastcat_titles.fcat)) {
      const suffix = props.registration.title.toUpperCase().replace(Constants.akc_fastcat_titles.fcat, "");
      const parsed = parseInt(suffix, 10);
      return isNaN(parsed) || parsed < 1 ? 1 : parsed;
    }
    return 1;
  });

  const [coOwners, setCoOwners] = useState<IDogCoownerInfo[]>([{
    coowner_type: "owner",
    display_name: "",
    address: {
      line1: "",
      line2: "",
      city: "",
      country: ""
    },
    email: "",
    phone: ""
  }]);

  const [steps, setSteps] = useState<string[]>(["Dog", "Registration", "Owner"]);

  const regPrefixOptions:IOption[] = [
    { id: Constants.registration_number_type.ukc_registered, value: Constants.registration_number_type.ukc_registered },
    { id: Constants.registration_number_type.ukc_temporary, value: Constants.registration_number_type.ukc_temporary },
    { id: Constants.registration_number_type.ukc_performance, value: Constants.registration_number_type.ukc_performance },
    { id: Constants.registration_number_type.akc_registered, value: Constants.registration_number_type.akc_registered },
    { id: Constants.registration_number_type.akc_pal, value: Constants.registration_number_type.akc_pal },
    { id: Constants.registration_number_type.akc_canine_partner, value: Constants.registration_number_type.akc_canine_partner },
    { id: Constants.registration_number_type.akc_foreign_reg, value: Constants.registration_number_type.akc_foreign_reg },
    { id: Constants.registration_number_type.isws_registered, value: Constants.registration_number_type.isws_registered },
    { id: Constants.registration_number_type.isws_ukc, value: Constants.registration_number_type.isws_ukc },
    { id: Constants.registration_number_type.isws_akc, value: Constants.registration_number_type.isws_akc },
    { id: Constants.registration_number_type.isws_ckc, value: Constants.registration_number_type.isws_ckc },
    { id: Constants.registration_number_type.isws_other, value: Constants.registration_number_type.isws_other }
  ];

  const [originalClasses, setOriginalClasses] = useState<any[]>();

  const handlerEnabled: string[] = [
    Constants.show_type.conformation,
    Constants.show_type.obedience,
    Constants.show_type.rally_obedience,
    Constants.show_type.fetch,
    Constants.show_type.fast_cat,
    Constants.show_type.water_dog,
    Constants.show_type.shed_dog,
  ];

  const hasVariations = () => {
    let hasVariation = breedList.filter((b: IBreed) => {
      return b.breed.toLowerCase() === breedSearch.toLowerCase() && b.variations !== undefined && b.variations.length > 0;
    });

    for (let i in hasVariation) {
      let hasAKC:any = hasVariation[i].variations?.filter((v: any) => { return v.name === Constants.sanctioning_club.AKC });

      if (hasAKC.length > 0) {
        // Allow "None" as a valid selection for AKC variations
        if (AKCVariation.id === Constants.none) {
          // "None" is now valid, so don't return false
          continue;
        }
      }

      let hasUKC:any = hasVariation[i].variations?.filter((v: any) => { return v.name === Constants.sanctioning_club.UKC });

      if (hasUKC.length > 0) {
        // Allow "None" as a valid selection for UKC variations
        if (UKCVariation.id === Constants.none) {
          // "None" is now valid, so don't return false
          continue;
        }
      }
    }

    return true;
  }

  useEffect(() => {
    const createId = () => {
      return Math.random().toString(36).substring(2, 8);
    }

    const getDogClasses = () => {
      let dogClasses = JSON.parse(JSON.stringify(props.registration.dog_classes));
      let nonLicensedId:string = "";

      // List of Nosework elements that can have multiple levels
      const noseworkElements = [
        Constants.dog_class_element.nosework_container,
        Constants.dog_class_element.nosework_exterior,
        Constants.dog_class_element.nosework_interior,
        Constants.dog_class_element.nosework_vehicle,
        Constants.dog_class_element.nosework_handler_descrimination
      ];

      for (let d in dogClasses) {
        if (nonLicensed.includes(dogClasses[d].show_element)) {
          if (nonLicensedId.length > 0) {
            dogClasses[d].id = nonLicensedId;
          } else {
            let _id = createId();
            dogClasses[d].id = _id;
            nonLicensedId = _id;
          }
        } else {
          if (helperService.timeSlotShows.includes(dogClasses[d].show_element)) {
            dogClasses[d].id = dogClasses[d].show_element;
            dogClasses[d].time_slot = props.registration.time_slot;
          } else if (dogClasses[d].show_element === Constants.show_type.country_rat_race) {
            // For Country Rat Race, create a unique ID based on show_element + level + show_id
            // This matches what the FormBuilder expects and avoids conflicts between multiple shows
            const showId = props.registration.show_id || 'default';
            dogClasses[d].id = `crr-${showId}-${dogClasses[d].level}`;
          } else if (noseworkElements.includes(dogClasses[d].show_element)) {
            // For Nosework, create a unique ID based on show_element + level
            // This allows the same element (e.g., Container) to be entered at multiple levels (Novice, Advanced, etc.)
            dogClasses[d].id = `${dogClasses[d].show_element}-${dogClasses[d].level}`;
          } else {
            // For classes with dropdown forms (Conformation, Altered Conformation, Junior Showmanship, etc.)
            // set the id to the show_element so getValue() can find them
            dogClasses[d].id = dogClasses[d].show_element;
          }
        }
      }

      return dogClasses;
    }

    // Spread ASFA registration-level fields onto every class object so FormBuilder
    // can read them via getIsChecked() / getValue() (which look inside classes[]).
    const getDogClassesWithAsfa = () => {
      const dogClasses = getDogClasses();
      const { asfa_first_trial_for_hound, asfa_first_time_entry, asfa_info_changed_since_last_trial, asfa_info_changed_regarding, asfa_dismissed_last_6_trials } = props.registration as any;
      const hasAsfaData = [asfa_first_trial_for_hound, asfa_first_time_entry, asfa_info_changed_since_last_trial, asfa_info_changed_regarding, asfa_dismissed_last_6_trials].some(v => v !== undefined);
      if (!hasAsfaData) return dogClasses;
      return dogClasses.map((dc: any) => ({
        ...dc,
        asfa_first_trial_for_hound,
        asfa_first_time_entry,
        asfa_info_changed_since_last_trial,
        asfa_info_changed_regarding,
        asfa_dismissed_last_6_trials,
      }));
    };

    const searchBreeds = () => {
      return new Promise<IBreed[]>((resolve) => {
        apiService.getBreeds().then((response) => {
          let breeds:IBreed[] = []; 

          response.forEach((b: IBreed) => {
            breeds.push(b);
          });

          resolve(breeds);
        }).catch((e: AxiosError) => {
          toast.show({
            title: "Dog Breeds",
            content: "Unable to get dog breeds",
            duration: 10000,
            errorDetails: e,
            type: "fail"
          });

          resolve([]);
        });
      });
    }

    if (!loaded) {
      if (props.event?.sanctioning_club === Constants.sanctioning_club.AKC) {
        setSteps(["Dog", "Co-Owners", "Registration", "Owner"]);
      }

      const hydrateVariation = () => {
        if (props.registration.dog.variation && props.registration.dog.variation.length > 0) {
          switch(props.event?.sanctioning_club) {
            case Constants.sanctioning_club.UKC:
              setUKCVariation({ value: props.registration.dog.variation, id: props.registration.dog.variation });
            break;
            case Constants.sanctioning_club.AKC:
              setAKCVariation({ value: props.registration.dog.variation, id: props.registration.dog.variation });
            break;
          }
        }
      }

      if (props.registration.dog) {
        hydrateVariation();
      }

      if (props.registration?.created_by) {
        const clubUserDogsRequest = apiService.getUserDogs(props.registration.created_by, props.registration.club_id);
        const userDogsRequest = apiService.getUserDogs(props.registration.created_by);
        Promise.all([clubUserDogsRequest, userDogsRequest])
          .then(([clubUserDogs, userDogs]) => {
            const allDogs = [...clubUserDogs, ...userDogs].filter((d) => d._id !== props.registration.dog.dog_id);
            setDogs(allDogs);

            // Pre-fill the companion reg number from their UKC club_registration if not already overridden
            const braceClass = props.registration.dog_classes?.find(
              (dc: IDogClass) => dc.level === Constants.dog_class_element_level.brace
            );
            if (braceClass?.extra && !braceClass.extra_registration_number) {
              const companion = allDogs.find((d) => d._id === braceClass.extra);
              if (companion) {
                const ukcReg = companion.club_registrations?.find(
                  (cr) => cr.sanctioning_club === Constants.sanctioning_club.UKC
                );
                if (ukcReg?.registration_number) {
                  setCompanionRegNum(ukcReg.registration_number);
                }
              }
            }
          })
          .catch(() => {
            toast.show({
              title: "Update Registration",
              content: "Couldn't load dogs, please try to refresh the page",
              duration: 10000,
              type: "fail"
            });
          })
      }

            // Get all shows for this event and find the specific one we need
      apiService.getClubEventShows(props.registration.club_id, props.registration.event_id, {}).then((allShows) => {
        // Find the specific show we need
        const targetShow = allShows.find(show => show._id === props.registration.show_id);
        if (targetShow) {
          setShow(targetShow);
        } else {
          // Try string comparison in case of type mismatch
          const targetShowStr = allShows.find(show => String(show._id) === String(props.registration.show_id));
          if (targetShowStr) {
            setShow(targetShowStr);
          }
        }
      }).catch((error) => {
        console.error("Failed to load show data:", error);
      });

      // Fetch score data to get jump heights
      if (props.event?._id) {
        apiService.getEventScores(props.event._id, {
          query: {
            "$and": [
              {
                attribute_name: "dog_id",
                attribute_value: {
                  operator: "$eq",
                  value: props.registration.dog_id
                }
              },
              {
                attribute_name: "show_id",
                attribute_value: {
                  operator: "$eq",
                  value: props.registration.show_id
                }
              }
            ]
          }
        }).then((scores) => {
          // Use the first score record to populate jump heights
          if (scores && scores.length > 0) {
            const scoreData = scores[0];
            
            if (scoreData.jump_height_type) {
              setJumpHeightType({ id: scoreData.jump_height_type, value: scoreData.jump_height_type });
            }
            
            if (scoreData.obedience_jump_height && !props.registration?.dog?.obedience_jump_height) {
              setOJumpHeight({ 
                id: scoreData.obedience_jump_height.toString(), 
                value: scoreData.obedience_jump_height.toString() + '"' 
              });
            }

            if (scoreData.akc_obedience_preferred_jump_height && !props.registration?.dog?.akc_obedience_preferred_jump_height) {
              setOPreferredJumpHeight({
                id: scoreData.akc_obedience_preferred_jump_height.toString(),
                value: scoreData.akc_obedience_preferred_jump_height.toString() + '"'
              });
            }
            
            if (scoreData.rally_jump_height && !props.registration?.dog?.rally_jump_height) {
              setRJumpHeight({ 
                id: scoreData.rally_jump_height.toString(), 
                value: scoreData.rally_jump_height.toString() + '"' 
              });
            }
            
            if (scoreData.agility_jump_height && !props.registration?.dog?.agility_jump_height) {
              setAgilityJumpHeight({ 
                id: scoreData.agility_jump_height.toString(), 
                value: scoreData.agility_jump_height.toString() + '"' 
              });
            }
          }
        }).catch((error) => {
          console.error("Failed to load score data:", error);
        });
      }

      const loadBreeds = async() => {
        let breeds:IBreed[] = await searchBreeds();
        setBreedList(breeds);

        if (props.registration.dog.breed) {
          setBreedSearch(props.registration.dog.breed);
          const match = breeds.find((b: IBreed) => b.breed.toLowerCase() === props.registration.dog.breed?.toLowerCase());
          if (match) {
            setSelectedBreed(match);
          }
        }
      }

      loadBreeds();
      setCoOwners(props?.registration.coowners || []);
      
      const initialDogClasses = getDogClassesWithAsfa();
      setDogClasses(initialDogClasses);
      
      // CRITICAL: Set originalClasses here from the actual database value
      // This prevents issues where FormBuilder's initial render modifies the classes
      setOriginalClasses(JSON.parse(JSON.stringify(initialDogClasses)));

      setLoaded(true);
    }
  }, [loaded, props.registration, props.event?.sanctioning_club]);

  const isValid = () => {
    if (!hasBreed()) {
      toast.show({
        title: "Dog Breed",
        content: "Please select a dog breed from the dropdown menu",
        duration: 10000,
        type: "fail"
      });

      switchToTab(0);
      return false;
    }

    if (!hasVariety()) {
      toast.show({
        title: "Dog Variety",
        content: "Please select a dog variety from the dropdown menu",
        duration: 10000,
        type: "fail"
      });

      switchToTab(0);
      return false;
    }

    let validAddress:IAddressError = isValidAddress(address);
    let validPhone:IAddressError = isValidPhone(phone);

    if (!validPhone.valid) {
      toast.show({
        title: "Update Registration",
        content: validPhone.message || "",
        duration: 10000,
        type: "fail"
      });

      switchToTab(2);
      return false;
    }

    if (!validAddress.valid) {
      toast.show({
        title: "Update Registration",
        content: validAddress.message || "",
        duration: 10000,
        type: "fail"
      });

      switchToTab(2);
      return false;
    }

    if (!isValidClasses(dogClasses)) {
      switchToTab(1);
      return false;
    }

    if (breedVariationExists() && !hasVariations()) {
      toast.show({
        title: "Update Registration",
        content: "Please select a dog variation from the dropdown list",
        duration: 10000,
        type: "fail"
      });

      switchToTab(0);
      return false;
    }

    if (!isValidAgent()) {
      if (!agentName) {
        let message = "Please type in an agent/handler name";
        
        if (usingNonAgent) {
          message = "Please type in a handler name";
        } else if (props.event?.sanctioning_club === Constants.sanctioning_club.UKC || 
                   props.event?.sanctioning_club === Constants.sanctioning_club.BHA) {
          message = "Please type in a handler name";
        }
        
        toast.show({
          title: "Update Registration",
          content: message,
          duration: 10000,
          type: "fail"
        });
      }
      // Water dog specific validations
      if (show?.show_type === Constants.show_type.water_dog) {
        if (!agentNumber) {
          toast.show({
            title: "Update Registration",
            content: "Please type in an agent/handler number",
            duration: 10000,
            type: "fail"
          });
        }
        if (!helperService.isValidEmail(agentEmail ?? "")) {
          toast.show({
            title: "Update Registration",
            content: "Please enter a valid agent/handler email",
            duration: 10000,
            type: "fail"
          });
        }
        if (!isPossiblePhoneNumber(agentPhone ?? "")) {
          toast.show({
            title: "Update Registration",
            content: "Please enter a valid agent/handler phone",
            duration: 10000,
            type: "fail"
          });
        }
      }

      switchToTab(2);
      return false;
    }


    // if (!isValidSteward()) {
    //   if (!stewardName) {
    //     toast.show({
    //       title: "Update Registration",
    //       content: "Please type in an steward name",
    //       duration: 10000,
    //       type: "fail"
    //     });
    //   }
    //   if (!helperService.isValidEmail(stewardEmail)) {
    //     toast.show({
    //       title: "Update Registration",
    //       content: "Please enter a valid steward email",
    //       duration: 10000,
    //       type: "fail"
    //     });
    //   }
    //   if (!isPossiblePhoneNumber(stewardPhone)) {
    //     toast.show({
    //       title: "Update Registration",
    //       content: "Please enter a valid steward phone",
    //       duration: 10000,
    //       type: "fail"
    //     });
    //   }

    //   switchToTab(2);
    //   return false;
    // }

    return true;
  }

  const updateRegistration = () => {
    setIsUpdatingRegistration(true);

    let payload:any = {
      show_registration: {
        using_agent: usingAgent,
        agent_name: agentName,
        agent_number: agentNumber,
        agent_email: agentEmail,
        agent_phone: agentPhone,
        using_non_agent: usingNonAgent,
        competing_owner_handler: competingOwnerHandler,
        substitute_name: stewardName,
        substitute_email: stewardEmail,
        substitute_phone: stewardPhone,
        leg_info: legInfo.value,
        wheelchair_type: wheelchairType.value,
        needs_measurement: needsMeasurement,
        plan_to_earn_title: planToEarnTitle,
        title: planToEarnTitle ? (selectedTitle.id === Constants.akc_fastcat_titles.fcat ? `${Constants.akc_fastcat_titles.fcat}${fcatNumber}` : selectedTitle.id) : undefined,
        date_received: dateReceived,
      },
      dog: {
        breed: selectedBreed?.breed ?? breedSearch,
        sex: gender,
        dob: dogDOB,
        weight: weight,
        height_at_withers: heightAtWithers,
        registered_name: name,
        call_name: callName,
        prefix_title: prefixTitle,
        suffix_title: suffixTitle,
        registration_number_type: regPrefix.id,
        registration_number: regNum,
        sire: sire,
        dam: dam,
        final_response: finalResponse,
        country_of_birth: countryOfBirth,
        obedience_jump_height: oJumpHeight.id ? parseFloat(oJumpHeight.id) : undefined,
        akc_obedience_preferred_jump_height: oPreferredJumpHeight.id ? parseFloat(oPreferredJumpHeight.id) : undefined,
        rally_jump_height: rJumpHeight.id ? parseFloat(rJumpHeight.id) : undefined,
        agility_jump_height: agilityJumpHeight.id ? parseFloat(agilityJumpHeight.id) : undefined,
        jump_height_type: jumpHeightType.id !== "None" ? jumpHeightType.id : undefined,
        breeder: breeder,
        variety: variety.id !== "None" ? variety.id : ""
      },
      owner: {
        display_name: displayName,
        email: email,
        phone: phone,
        address: address,
        ...(isJuniorRegistration ? { junior_number: juniorNumber.trim() } : {})
      },
      coowners: coOwners.map((c: IDogCoownerInfo, index: number) => {
        c.display_name = c.first_name +" "+ c.last_name;
        return c;
      })
    };

    if (!breedVariationExists()) {
      payload.dog.variation = "";
    } else if (AKCVariation.id !== Constants.none || UKCVariation.id !== Constants.none) {
      payload.dog.variation = "";

      if (AKCVariation.id !== Constants.none) {
        payload.dog.variation = AKCVariation.id;
      }

      if (UKCVariation.id !== Constants.none) {
        payload.dog.variation = UKCVariation.id;
      }
    }

    // Only send jump_height_type for show types that actually need it
    const showTypesNeedingJumpHeight = [
      Constants.show_type.obedience,
      Constants.show_type.rally_obedience,
      Constants.show_type.agility
    ];
    
    if (showTypesNeedingJumpHeight.includes(props.registration.show_type) && jumpHeightType.value !== "None") {
      payload.dog.jump_height_type = jumpHeightType.value;
    }

    // Include dog_classes if they changed OR if there's a brace companion override to persist
    const hasBraceClass = dogClasses.some((dc: any) => dc.level === Constants.dog_class_element_level.brace);
    const classesChanged = JSON.stringify(originalClasses) !== JSON.stringify(dogClasses);

    if (classesChanged || hasBraceClass) {
      const classesWithCompanion = dogClasses.map((dc: any) => {
        if (dc.level === Constants.dog_class_element_level.brace) {
          return { ...dc, extra_registration_number: companionRegNum };
        }
        return dc;
      });
      payload.dog.dog_classes = classesWithCompanion;
    }

    // Extract ASFA fields from dogClasses (FormBuilder stores answers there)
    // and include them in the show_registration update payload.
    const asfaClass = dogClasses.find((dc: any) =>
      dc.asfa_first_trial_for_hound !== undefined ||
      dc.asfa_first_time_entry !== undefined ||
      dc.asfa_info_changed_since_last_trial !== undefined ||
      dc.asfa_dismissed_last_6_trials !== undefined
    ) as any;
    if (asfaClass) {
      payload.show_registration.asfa_first_trial_for_hound = asfaClass.asfa_first_trial_for_hound;
      payload.show_registration.asfa_first_time_entry = asfaClass.asfa_first_time_entry;
      payload.show_registration.asfa_info_changed_since_last_trial = asfaClass.asfa_info_changed_since_last_trial;
      payload.show_registration.asfa_info_changed_regarding = asfaClass.asfa_info_changed_regarding;
      payload.show_registration.asfa_dismissed_last_6_trials = asfaClass.asfa_dismissed_last_6_trials;
    }

    for (let d in dogClasses) {
      if (payload.dog && helperService.timeSlotShows.includes(dogClasses[d].show_element)) {
        payload.dog.time_slot = dogClasses[d].time_slot;
        break;
      }
    }

    if (props.event && isValid()) {
      apiService.updateShowRegistration(props.event._id, props.registration._id, payload).then((response) => {
        toast.show({
          title: "Update Registration",
          content: "Successfully updated the registration",
          duration: 10000,
          type: "success"
        });

        props.onComplete();
      }).catch((error) => {
        toast.show({
          title: "Update Registration",
          content: error.response?.data?.message ? error.response.data.message : "There was an error updating the registration, please try again",
          //content: "There was an error updating the registration, please try again",
          duration: 10000,
          type: "fail"
        });
      }).finally(() => {
        setIsUpdatingRegistration(false);
      })
    } else {
      setIsUpdatingRegistration(false);
    }
  }

  const isValidClasses = (dogClasses: IDogClass[]) => {
    let valid = true;

    for (let dc in dogClasses) {
      // Check for any falsy section value (undefined, null, empty string) OR explicit "None"
      if (props.registration.show_type === Constants.show_type.nosework && 
          (!dogClasses[dc].section || dogClasses[dc].section === "None" || dogClasses[dc].section.trim() === "")) {
        toast.show({
          title: "Registration Information",
          content: "Please select a section for " + dogClasses[dc].show_element,
          duration: 10000,
          type: "fail"
        });

        valid = false;
      }

      if ([Constants.show_type.obedience, Constants.show_type.rally_obedience].includes(props.registration.show_type)) {
        if (dogClasses[dc].level === Constants.dog_class_element_level.team && dogClasses[dc]?.extra_arr?.length !== 3) {
          toast.show({
            title: "Registration Information",
            content: "Please select 3 teammates for " + dogClasses[dc].show_element,
            duration: 10000,
            type: "fail"
          });

          valid = false;
        }

        if ([Constants.dog_class_element_level.brace, Constants.dog_class_element_level.pairs, Constants.dog_class_element_level.novice_pairs, Constants.dog_class_element_level.advanced_pairs].includes(dogClasses[dc].level) && !dogClasses[dc]?.extra) {
          toast.show({
            title: "Registration Information",
            content: "Please select a teammate for " + dogClasses[dc].show_element,
            duration: 10000,
            type: "fail"
          });

          valid = false;
        }
      }

    }

    return valid;
  }

  const getBreed = useCallback((v: string, breeds?: IBreed[]) => {
    let hasBreed = breeds ? breeds.filter((b: IBreed) => { return b.breed.toLowerCase() === v.toLowerCase() }) : breedList.filter((b: IBreed) => { return b.breed.toLowerCase() === v.toLowerCase() });

    if (hasBreed.length > 0) {
      return hasBreed[0];
    }

    return undefined;
  }, [breedList]);

  const hasBreed = () => {
    // Must have selected from dropdown - typing alone is not allowed
    if (selectedBreed && selectedBreed.breed) {
      return true;
    }

    toast.show({
      title: "Breed Selection",
      content: "Please select a dog breed from the dropdown list. Typed breeds are not allowed.",
      duration: 10000,
      type: "fail"
    });

    return false;
  }

  const hasVariety = () => {
    // Allow "None" as a valid selection even when varieties exist
    if (variety.id === "None") {
      return true;
    }
    
    if (selectedBreed !== undefined && selectedBreed.variety !== undefined && selectedBreed.variety.length > 0) {
      // Check if the variety exists in any of the club-specific variety arrays
      const allVarieties = selectedBreed.variety.flatMap(v => v.values || []);
      if (allVarieties.includes(variety.id)) {
        return true;
      }
    } else if (selectedBreed !== undefined && (selectedBreed.variety === undefined || selectedBreed.variety.length === 0)) {
      return true;
    }

    return false;
  }

  const isValidAgent = () => {
    // Check professional agent/handler validation
    if (usingAgent) {
      if (props.registration.show_type === Constants.show_type.water_dog) {
        return agentName && agentNumber && isPossiblePhoneNumber(agentPhone ?? "") && helperService.isValidEmail(agentEmail ?? "");
      }
      // For all other cases, only require agent/handler name (number is optional)
      return agentName;
    }
    
    // Check non-agent handler validation (just needs name, no number required)
    if (usingNonAgent) {
      return agentName;
    }
    
    return true;
  }

  // const isValidSteward = () => {
  //   if (props.registration.show_type === Constants.show_type.water_dog) {
  //     return stewardName && isPossiblePhoneNumber(stewardPhone) && helperService.isValidEmail(stewardEmail);
  //   }
  //   return true;
  // }

  const breedVarietyExists = () => {
    let hasVariety = breedList.filter((b: IBreed) => { 
      return b.breed.toLowerCase() === breedSearch.toLowerCase() && 
             b.variety !== undefined && 
             b.variety.length > 0 &&
             b.variety.some(v => v.values && v.values.length > 0);
    });

    if (hasVariety.length > 0) {
      return true;
    }

    return false;
  }

  const breedVariationExists = () => {
    let hasVariation = breedList.filter((b: IBreed) => {
      return b.breed.toLowerCase() === breedSearch.toLowerCase() && 
      b.variations !== undefined && b.variations.length > 0 && 
      b.variations.filter((va: any) => {
        return va.name === props.event?.sanctioning_club }).length > 0;
      }
    );

    if (hasVariation.length > 0) {
      return true;
    }

    return false;
  }

  const getVariationOptions = (sanctioning_club: string) => {
    let options:IOption[] = [
      { id: "None", value: "None" }
    ];

    let _variations:any = selectedBreed?.variations?.filter((v: any) => { return v.name === sanctioning_club });

    for (let i in _variations) {
      for (let v in _variations[i].values) {
        options.push({ id: _variations[i].values[v], value: _variations[i].values[v] });
      }
    }

    return options;
  }

  const setVariation = (sc: string, value: IOption) => {
    switch (sc) {
      case Constants.sanctioning_club.AKC:
        setAKCVariation(value);
      break;
      case Constants.sanctioning_club.UKC:
        setUKCVariation(value);
      break;
    }
  }

  return (
    <div className="EditRegistration">
      <div className="title">
        <span>Edit Registration</span>
        <span>{props.registration.dog.call_name}</span>
      </div>
      <StepForm activeTab={activeTab} next={switchToTab} steps={steps} onSubmit={updateRegistration} isLoading={isUpdatingRegistration}>
        <Step>
          <div className="stepTitle">Dog Information</div>
          <p>Update the dogs information. This won't affect the users account and only applies to this individual registration.</p>
          <div className="row editDog">
            <Input required label="Call Name" id="callName" type="text" onChange={(e) => { e.stopPropagation(); setCallName(e.target.value); }} placeholder="What's your dogs call name?" defaultValue={callName} />
            <Input required label="Registered Name" id="registeredName" type="text" onChange={(e) => { e.stopPropagation(); setName(e.target.value); }} placeholder="What's your dogs registered name?" defaultValue={name} />
          </div>
          <div className="row editDog">
            <Input label="Prefix Title" id="prefixTitle" type="text" onChange={(e) => { e.stopPropagation(); setPrefixTitle(e.target.value); }} placeholder="What's the dogs prefix title?" defaultValue={prefixTitle} />
            <Input label="Suffix Title" id="suffixTitle" type="text" onChange={(e) => { e.stopPropagation(); setSuffixTitle(e.target.value); }} placeholder="What's the dogs suffix title?" defaultValue={suffixTitle} />
          </div>
          <SearchDown
            required
            label="Breed"
            icon={star}
            id="breed"
            type="text"
            onChange={(e) => {
              e.stopPropagation();
              const value = e.target.value;
              setBreedSearch(value);
              const match = breedList.find((b: IBreed) => b.breed.toLowerCase() === value.trim().toLowerCase());
              const stillMatchesSelected = selectedBreed && value.trim().toLowerCase() === selectedBreed.breed.toLowerCase();
              if (stillMatchesSelected && match) {
                setSelectedBreed(match);
              } else if (selectedBreed && !stillMatchesSelected) {
                setSelectedBreed(undefined);
                setVariety({ id: "None", value: "None" });
              } else {
                setSelectedBreed(undefined);
              }
            }}
            onSelect={(value: string) => {
              const match = breedList.find((b: IBreed) => b.breed.toLowerCase() === value.trim().toLowerCase());
              if (match) {
                setBreedSearch(match.breed);
                setSelectedBreed(match);
                setVariety({ id: "None", value: "None" });
              }
            }}
            placeholder="What is your dog's breed?"
            onSearch={() => {  }}
            searchResults={breedList.filter((b: IBreed) => {
              if (breedSearch && breedSearch.length > 0 && b.breed.toLowerCase().includes(breedSearch.toLowerCase())) {
                return b.breed;
              } else {
                return false;
              }}).map((b: IBreed) => b.breed)
            }
            defaultValue={breedSearch}
          />
          <>{breedVarietyExists() && <div className="varietyWrap">
            <Dropdown
              value={variety}
              label={"Variety"}
              onChange={(e: any, value: IOption) => { setVariety(value); }}
              options={[
                { id: "None", value: "None" },
                ...(selectedBreed?.variety?.flatMap(v => v.values?.map((val: string) => ({ id: val, value: val })) || []) || [])
              ]}
              placeholder=""
            />
          </div>}</>
          <>{loaded && breedVariationExists() && selectedBreed?.variations?.map((v: any, index: any) => {
              return(
                <div key={"varation " + index} className="varietyWrap">
                  <Dropdown
                    value={props.event?.sanctioning_club === Constants.sanctioning_club.AKC ? AKCVariation : UKCVariation}
                    label={v.name + " Variation"}
                    onChange={(e: any, value: IOption) => { setVariation((props.event?.sanctioning_club || "AKC"), value); }}
                    options={getVariationOptions(props.event?.sanctioning_club || "AKC")}
                    placeholder=""
                  />
                </div>
              );
          })}</>
          <div className="breedError">
            <div className="breedErrorInner">
              <div className="breedErrorContent">
                <p>Can't find the breed you're looking for?  <a href={"mailto:info@eagerdog.com?subject=Missing Dog Breed: " + breedSearch}>Contact us</a> and let us know.</p>
              </div>
            </div>
          </div>
          <div className="row editDog">
            <Dropdown className="prefix" value={regPrefix} label={"Prefix"} onChange={(e: any, value: IOption) => { setRegPrefix(value); }} options={regPrefixOptions} placeholder="" />
            <Input required icon={num} label="Registration Number" id="regNum" type="text" onChange={(e) => { e.stopPropagation(); setRegNum(e.target.value); }} placeholder="What's the dogs Registration number?" defaultValue={regNum} />
          </div>
          <div className="registrationHelpText">
            Make sure to include any prefix letters in your registration number
          </div>
          <DatePick label="Date of Birth" maxDate={new Date()} onChange={setDogDOB} value={dogDOB} />
          <div className="gender">
            <div className="checkboxWrap">
              <label htmlFor="male">
                <div className="checkmarkWrap">
                  <input type="checkbox" id={"male"} name={Constants.dog_sex.male} checked={gender === Constants.dog_sex.male ?  true : false} onChange={() => { setGender(Constants.dog_sex.male); }} />
                  <span className="checkmark"></span>
                </div>
                <span className="name">Male</span>
              </label>
            </div>
            <div className="checkboxWrap">
              <label htmlFor="female">
                <div className="checkmarkWrap">
                  <input type="checkbox" id={"female"} name={Constants.dog_sex.female} checked={gender === Constants.dog_sex.female ?  true : false} onChange={() => { setGender(Constants.dog_sex.female); }} />
                  <span className="checkmark"></span>
                </div>
                <span className="name">Female</span>
              </label>
            </div>
          </div>
          <div className="row editDog">
            <Input label="Weight (lbs)" icon={scale} id="weight" type="number" step="0.01" min="1" onChange={(e) => { e.stopPropagation(); setWeight(Number(e.target.value)); }} placeholder="What's your dogs weight? (optional)" defaultValue={weight as any} />
            <Input label="Height at Withers (inches)" icon={num} id="heightAtWithers" type="number" step="0.01" min="1" onChange={(e) => { 
              e.stopPropagation(); 
              const value = e.target.value;
              setHeightAtWithersInput(value);
              const numValue = parseFloat(value);
              setHeightAtWithers(isNaN(numValue) ? undefined : numValue);
            }} placeholder="What's your dogs height at withers? (optional)" value={heightAtWithersInput} />
          </div>
          <div className="row editDog">
            <Input label="Sire" id="sire" type="text" onChange={(e) => { e.stopPropagation(); setSire(e.target.value); }} placeholder="Who's your dogs Sire?" defaultValue={sire} />
            <Input label="Dam" id="dam" type="text" onChange={(e) => { e.stopPropagation(); setDam(e.target.value); }} placeholder="Who's your dogs Dam?" defaultValue={dam} />
          </div>
          <div className="row editDog">
            <Input label="Final Response" id="finalResponse" type="text" onChange={(e) => { e.stopPropagation(); setFinalResponse(e.target.value); }} placeholder="What's your dogs final response?" defaultValue={finalResponse} />
            <Input label="Country of Birth" id="countryOfBirth" type="text" onChange={(e) => { e.stopPropagation(); setCountryOfBirth(e.target.value); }} placeholder="Who's your dogs country of birth?" defaultValue={countryOfBirth} />
          </div>
          <div className="row editDog">
            <Input label="Breeder" id="breeder" type="text" onChange={(e) => { e.stopPropagation(); setBreeder(e.target.value); }} placeholder="Who's your dogs breeder?" defaultValue={breeder} />
            <Dropdown value={jumpHeightType} label={"UKC Jump Height Type"} onChange={(e: any, value: IOption) => { setJumpHeightType(value); }} options={dogHeightTypes} placeholder="" />
          </div>
          <>{props.event?.sanctioning_club === Constants.sanctioning_club.AKC && (
            <div className="agentWrap">
              <Checkbox
                id={"needsMeasurement" + props.registration.dog.call_name + props.registration.show_id}
                value={needsMeasurement}
                label={"Does this dog need to be measured at trials?"}
                onChange={() => { setNeedsMeasurement(!needsMeasurement); }}
              />
            </div>
          )}</>
          <div className="row editDog">
            <Dropdown value={oJumpHeight} label={"AKC Obedience Jump Height"} onChange={(_e: any, value: IOption) => { setOJumpHeight({ id: value.id, value: value.value }); }} options={dogObedienceHeights} placeholder="" />
            <Dropdown value={oPreferredJumpHeight} label={"AKC Preferred Obedience Jump Height"} onChange={(_e: any, value: IOption) => { setOPreferredJumpHeight({ id: value.id, value: value.value }); }} options={dogObedienceHeights} placeholder="" />
          </div>
          <div className="row editDog">
            <Dropdown value={rJumpHeight} label={"AKC Rally Jump Height"} onChange={(_e: any, value: IOption) => { setRJumpHeight({ id: value.id, value: value.value }); }} options={dogRallyHeights} placeholder="" />
            <Dropdown value={agilityJumpHeight} label={"AKC Agility Jump Height"} onChange={(_e: any, value: IOption) => { setAgilityJumpHeight({ id: value.id, value: value.value }); }} options={dogAgilityHeights} placeholder="" />
          </div>
        </Step>
        <>{(props.event?.sanctioning_club === Constants.sanctioning_club.AKC) && <Step>
          <React.Fragment>
            <div className="stepTitle">Co-Owner Information</div>
            <p>Update the dogs co-owner information. This won't affect the users account and only applies to this individual registration.</p>
            {loaded && <OwnerList owners={coOwners} onChange={(newOwners: IDogCoownerInfo[]) => { setCoOwners(newOwners); }} />}
          </React.Fragment>
        </Step>}</>
        <Step onSubmit={() => { return isValidClasses(dogClasses); }}>
          <div className="stepTitle">Registration Information</div>
          <p>Update the dogs registration information. You'll only be able to modify elements and levels that the dog owner has already selected and paid for.</p>
          <div className="dateReceivedWrap">
            <DatePick
              label="Date Received (MM/DD/YYYY)"
              value={dateReceived}
              onChange={(date: Date) => { setDateReceived(date); }}
            />
            <p className="dateReceivedHelper">Select the date this registration was received for proper pricing and waitlist calculations.</p>
          </div>
          <>{show !== undefined &&
            <>
              {(props.registration.show_type === Constants.show_type.fast_cat ||
                props.registration.show_type === Constants.show_type.cat) &&
                <div className="agentWrap">
                  <Checkbox
                    id={"planToEarnTitle" + props.registration.dog.call_name + props.registration.show_id}
                    value={planToEarnTitle}
                    label={props.registration.dog.call_name + " is Planning to Earn a Title"}
                    onChange={() => { setPlanToEarnTitle(!planToEarnTitle); }}
                  />
                  {planToEarnTitle &&
                    <Dropdown
                      className="titleDrop"
                      value={selectedTitle}
                      label="Title"
                      onChange={(e, value: IOption) => { setSelectedTitle(value); if (value.id === Constants.akc_fastcat_titles.fcat) setFcatNumber(1); }}
                      options={getTitleOptionsForShowType(props.registration.show_type)}
                      placeholder=""
                    />
                  }
                  {planToEarnTitle && selectedTitle.id === Constants.akc_fastcat_titles.fcat &&
                    <div style={{ maxWidth: "300px", marginBottom: "20px" }}>
                      <Input type="number" label="FCAT Number (1–100)" min={1} max={100} defaultValue={fcatNumber.toString()} onBlur={(e: any) => { const n = parseInt(e.target.value, 10); setFcatNumber(isNaN(n) || n < 1 ? 1 : Math.min(100, n)); }} />
                    </div>
                  }
                </div>
              }
              {props.event && [Constants.sanctioning_club.AKC].includes(props.event.sanctioning_club) && [
                Constants.show_type.conformation,
                Constants.show_type.herding,
                Constants.show_type.pre_trial_herding,
                Constants.show_type.instinct_test_herding,
                Constants.show_type.agility
              ].includes(props.registration.show_type) && props.registration.dog &&
                <div className="agentWrap">
                  <div className="agent">
                    <Checkbox
                      disabled={usingAgent}
                      id={"usingNonAgent" + props.registration.dog.call_name + props.registration.show_id}
                      value={!!usingNonAgent}
                      label={"I'm using a non-agent handler for " + props.registration.dog.call_name}
                      onChange={() => { 
                        setUsingNonAgent(!usingNonAgent); 
                        if (!usingNonAgent) setCompetingOwnerHandler(false);
                      }}
                    />
                  </div>
                  {usingNonAgent && <div className="agent">
                    <Input
                      defaultValue={agentName}
                      label="Non-agent Handler Name"
                      placeholder="Enter your non-agent handler name"
                      onChange={(e) => { setAgentName(e.target.value); }}
                    />
                  </div>}
                  {props.registration.show_type === Constants.show_type.conformation && <div className="agent">
                    <Checkbox
                      disabled={usingAgent || usingNonAgent}
                      id={"competingOwnerHandler" + props.registration.dog.call_name + props.registration.show_id}
                      value={!!competingOwnerHandler}
                      label={"Are you participating in Owner/Handler"}
                      onChange={() => { setCompetingOwnerHandler(!competingOwnerHandler); }}
                    />
                  </div>}
                </div>
              }

              {props.event?.sanctioning_club === Constants.sanctioning_club.AKC && props.registration.show_type === Constants.show_type.agility && <div style={{ marginBottom: 30 }}>
                <Checkbox id="dog-need-measure" label="Does this dog need to be measured at trials?" value={needsMeasurement} onChange={() => { setNeedsMeasurement(!needsMeasurement); }} />
              </div>}

              {props.event?.sanctioning_club === Constants.sanctioning_club.C_WAGS && props.registration.show_type === Constants.show_type.scent && <div style={{ marginBottom: 30 }}>
                <Dropdown id="dog-wheelchair" label="Handler using wheelchair" options={dogWheelchairOptions} value={wheelchairType} onChange={(_e, value: IOption) => { setWheelchairType(value) }} placeholder="" />
              </div>}

              {props.event && [Constants.sanctioning_club.AKC, Constants.sanctioning_club.PWDCA, Constants.sanctioning_club.UKC].includes(props.event.sanctioning_club) && props.registration.dog && handlerEnabled.includes(props.registration.show_type) &&
                <div className="agentWrap">
                  <Checkbox
                    disabled={usingNonAgent}
                    id={"usingAgent" + props.registration.dog.call_name + props.registration.show_id}
                    value={!!usingAgent}
                    label={props.event.sanctioning_club === Constants.sanctioning_club.UKC ? "Someone other than the owner is handling " + props.registration.dog.call_name : "I'm using an agent handler for " + props.registration.dog.call_name}
                    onChange={() => { 
                      setUsingAgent(!usingAgent); 
                      if (!usingAgent) setCompetingOwnerHandler(false);
                    }}
                  />
                  {usingAgent && <>
                    <Input
                      defaultValue={agentName}
                      label={props.event.sanctioning_club === Constants.sanctioning_club.UKC ? "Handler Name" : "Agent/Handler Name"}
                      placeholder={props.event.sanctioning_club === Constants.sanctioning_club.UKC ? "Enter handler name" : "Enter your agent handler name"}
                      onChange={(e) => { setAgentName(e.target.value); }}
                    />
                    {props.event.sanctioning_club !== Constants.sanctioning_club.UKC &&
                      <Input
                        defaultValue={agentNumber}
                        label="Agent/Handler Number"
                        placeholder="Enter your agent handler number"
                        onChange={(e) => { setAgentNumber(e.target.value); }}
                      />
                    }
                    {props.event.sanctioning_club === Constants.sanctioning_club.PWDCA && <>
                      <Input
                        defaultValue={agentEmail}
                        label="Agent/Handler Email"
                        placeholder="Enter your agent handler email"
                        onChange={(e) => { setAgentEmail(e.target.value); }}
                      />
                      <Phone
                        value={agentPhone}
                        label="Agent/Handler Phone"
                        placeholder="Enter your agent handler phone"
                        onChange={(value: string) => { setAgentPhone(value); }}
                      />
                    </>}
                  </>}
                </div>
              }
              {props.event?.sanctioning_club === Constants.sanctioning_club.PWDCA && <div className="agentWrap">
                <Input
                  defaultValue={stewardName}
                  label="Substitue Steward Name"
                  placeholder="Enter your substitue name"
                  onChange={(e) => { setStewardName(e.target.value); }}
                />
                <Phone
                  value={stewardPhone}
                  label="Substitue Steward Phone Number"
                  placeholder="Enter your substitue phone number"
                  onChange={(value: string) => { setStewardPhone(value); }}
                />
                <Input
                  defaultValue={stewardEmail}
                  label="Substitue Steward Email"
                  placeholder="Enter your substitue email"
                  onChange={(e) => { setStewardEmail(e.target.value); }}
                />
              </div>}
              <FormBuilder
                edit
                show={show}
                event={props.event}
                dog={{ ...props.registration.dog, ...{ dob: dogDOB } }}
                classes={dogClasses}
                isJunior={props.registration.owner.is_junior}
                userDogs={dogs}
                onChange={(newClasses: IDogClass[]) => {
                  setDogClasses(newClasses);
                }}
              />
              {props.event?.sanctioning_club === Constants.sanctioning_club.UKC &&
                dogClasses.some((dc: any) => dc.level === Constants.dog_class_element_level.brace) && (
                <div className="companionRegWrap">
                  <div className="companionRegTitle">Brace Companion Dog</div>
                  <Input
                    label="Companion UKC Registration #"
                    id="companionRegNum"
                    type="text"
                    placeholder="Companion dog's UKC registration number"
                    defaultValue={companionRegNum}
                    onChange={(e) => { setCompanionRegNum(e.target.value); }}
                  />
                  <p className="companionRegHelper">This overrides the companion dog's registration number on the judge's book for this entry only.</p>
                </div>
              )}
              {props.event?.sanctioning_club === Constants.sanctioning_club.PWDCA &&
                [
                  Constants.dog_class_element.working,
                  Constants.dog_class_element.courier,
                  Constants.dog_class_element.master
                ].includes(dogClasses?.[0]?.level) &&
                <Dropdown
                  value={legInfo}
                  label="What leg are you working on?"
                  onChange={(_e, option: IOption) => { setLegInfo(option); }}
                  options={getWorkingLegOptions(dogClasses)}
                  placeholder=""
                />
              }
            </>
          }</>
        </Step>
        <Step>
          <div className="stepTitle">Owner Information</div>
          <p>Update the owner information. This won't affect the users account and only applies to this individual registration.</p>
          <div className="row editDog">
            <Input required label="Display Name" id="displayName" type="text" onChange={(e) => { e.stopPropagation(); setDisplayName(e.target.value); }} placeholder="What's the owners name?" defaultValue={displayName} />
            <Input required label="Email" id="email" type="email" onChange={(e) => { e.stopPropagation(); setEmail(e.target.value); }} placeholder="What's the owners email?" defaultValue={email} />
          </div>
          <UserAddress user={props.registration.owner} onChange={(newAddress: IAddress, newPhone: string) => { setAddress(newAddress); setPhone(newPhone); }}/>
          <>{isJuniorRegistration && (
            <div className="row editDog juniorNumberRow">
              <Input
                label={`${props.event?.sanctioning_club ? props.event.sanctioning_club + " " : ""}Junior Number`}
                id="juniorNumber"
                type="text"
                onChange={(e) => { e.stopPropagation(); setJuniorNumber(e.target.value); }}
                placeholder="What's the junior handler's number?"
                defaultValue={juniorNumber}
              />
            </div>
          )}</>
        </Step>
      </StepForm>
    </div>
  );
};

export default EditRegistration;